/**
 * 我的日曆頁面
 * EE 商業模組，完整 SOP ceremony：usePermission gating、MUI Dialog、useMessage
 * 使用 FullCalendar（月/週/日 + rrule plugin）
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import googleCalendarPlugin from '@fullcalendar/google-calendar';
import zhTwLocale from '@fullcalendar/core/locales/zh-tw';
import enLocale from '@fullcalendar/core/locales/en-gb';
import type { EventClickArg, EventDropArg, EventInput } from '@fullcalendar/core';
import {
  Box, Button, TextField, Typography, IconButton, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox, CircularProgress, Alert,
  Autocomplete, Chip, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
  Tune as TuneIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  UploadFile as UploadFileIcon,
  Cloud as CloudIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { useSystem } from '../contexts/SystemContext';
import { useAuth } from '../contexts/AuthContext';
import FunctionPageHeader from '../components/FunctionPageHeader';
import {
  getEvents, createEvent, updateEvent, deleteEvent, notifyEvent,
  uploadAttachment, pickAttachment, removeAttachment,
  getMyScopes, listAssistants, addAssistant, revokeAssistant, checkConflict,
  getMyCalendarConfig, getFreeBusy,
  type CalendarScope, type CalendarAssistant, type ConflictItem,
} from '../services/eeMyCalendarService';
import FilePicker from '../components/FilePicker/FilePicker';
import RRuleBuilder from '../components/calendar/RRuleBuilder';
import myDriveService, { type DriveFile } from '../pages/ee/myDrive/services/myDriveService';
import axiosInstance from '../api/axios';
import { getContacts } from '../services/eeMyContactsService';
import { getMemberOptions, type MemberOption } from '../services/tenantUsersService';
import { getMeeting } from './ee/officeMeeting/services/officeMeetingService';
import { getMyLocations, type MyLocation } from '../services/eeMyLocationsService';
import { toDateTimeLocalValue, dateTimeLocalToUtcIso, getDisplayTimeZone } from '../utils/dateHelper';
import { EeMyCalendar, EeMyCalendarCreate, EeMyCalendarUpdate } from '../types/eeMyCalendar';
import type { EeMyContact } from '../types/eeMyContacts';

const FUNC_CODE = 'ee_my_calendar';

const COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0288d1', '#5d4037'];

// Google 公開假日日曆 ID
const HOLIDAY_CALENDARS: Record<string, string> = {
  TW: 'zh.taiwan#holiday@group.v.calendar.google.com',
  US: 'en.usa#holiday@group.v.calendar.google.com',
  JP: 'ja.japanese#holiday@group.v.calendar.google.com',
  CN: 'zh.china#holiday@group.v.calendar.google.com',
};

const HOLIDAY_META: [string, string][] = [
  ['TW', '🇹🇼 台灣'],
  ['US', '🇺🇸 美國'],
  ['JP', '🇯🇵 日本'],
  ['CN', '🇨🇳 中國'],
];

const DEFAULT_HOLIDAY_COLORS: Record<string, string> = {
  TW: '#ff6f00',
  US: '#1565c0',
  JP: '#c62828',
  CN: '#b71c1c',
};

// datetime-local 顯示值：以租戶時區呈現（DISPLAY_TZ 由 SystemContext 灌 tenantBranding.timezone）
const toLocalDatetime = (iso: string): string => toDateTimeLocalValue(iso);

// FullCalendar rrule plugin 會忽略事件的 start/end，只認 rrule 字串內的 DTSTART + duration。
// DTSTART 用**租戶時區的牆上時間（floating·不加 Z）**：rrule 的 BYDAY/BYMONTHDAY 以該時間判斷，
// 若轉 UTC（台灣 00:00–07:59 的行程）星期/日期會位移一天（K1）。floating 無時區換算 → 星期恆正確。
const toICalFloating = (iso: string, tz: string): string => {
  const [d, t] = toDateTimeLocalValue(iso, tz).split('T');   // 'YYYY-MM-DD','HH:mm'（租戶時區）
  return `${d.replace(/-/g, '')}T${t.replace(':', '')}00`;   // 'YYYYMMDDTHHMMSS'
};
const toICalFloatingDate = (iso: string, tz: string): string =>
  toDateTimeLocalValue(iso, tz).split('T')[0].replace(/-/g, '');   // 'YYYYMMDD'（租戶時區日期·不少一天）

const emptyForm = (): EeMyCalendarCreate => ({
  summary: '',
  description: '',
  location: '',
  dtstart: new Date().toISOString(),
  // duration 預設 1 小時（porsche 2026-08-27）
  dtend: new Date(Date.now() + 3600_000).toISOString(),
  is_all_day: false,
  rrule: '',
  status: 'confirmed',
  color: COLORS[0],
  is_active: true,
  is_meeting: false,
  public_meeting: true,
});

/* ── 附件列表（已存 fileIds 走 metadata；pending 為新增事件的本地緩衝）──────── */
const AttachmentList: React.FC<{
  fileIds: number[];
  pendingUploads?: File[];
  pendingPicks?: number[];
  pendingRemovals?: number[];                                          // fileIds 內、待移除（儲存才 commit）
  onRemove: (fileId: number) => void;                                  // 標記移除（不打 backend）
  onRestore?: (fileId: number) => void;                                // 從 pendingRemovals 復原
  onRemovePending?: (idx: number, type: 'upload' | 'pick') => void;    // local only
}> = ({ fileIds, pendingUploads = [], pendingPicks = [], pendingRemovals = [], onRemove, onRestore, onRemovePending }) => {
  const { t } = useTranslation();
  const [metas, setMetas] = useState<DriveFile[]>([]);

  useEffect(() => {
    const allIds = [...fileIds, ...pendingPicks];
    if (allIds.length === 0) { setMetas([]); return; }
    myDriveService.getFilesMeta(allIds).then(setMetas).catch(() => setMetas([]));
  }, [fileIds, pendingPicks]);

  const preview = async (id: string | number) => {
    try {
      const r = await axiosInstance.get(`/api/ee/my-drive/files/${id}/preview`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
    } catch { /* silent ok: 預覽失敗不阻斷編輯 */ }
  };

  const empty = fileIds.length === 0 && pendingUploads.length === 0 && pendingPicks.length === 0;
  if (empty) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('myCalendar.attachmentEmpty', '目前沒有附件')}
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {/* 已存附件：未標記移除 → 正常（藍/灰框，可預覽 + 標記移除）；已標記 → 灰 strikethrough + 復原 */}
      {fileIds.map(fid => {
        const m = metas.find(x => x.id === fid);
        const marked = pendingRemovals.includes(fid);
        return (
          <Box key={`f-${fid}`} display="flex" alignItems="center" gap={1}
               sx={{ px: 1, py: 0.5, bgcolor: 'grey.50', borderRadius: 1, opacity: marked ? 0.5 : 1 }}>
            <InsertDriveFileIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ flex: 1, textDecoration: marked ? 'line-through' : 'none' }} noWrap>
              {m?.filename ?? `#${fid}`}
            </Typography>
            {marked ? (
              <>
                <Chip size="small" color="warning" variant="outlined" label={t('myCalendar.pendingRemoval', '待移除')} />
                <Tooltip title={t('myCalendar.restore', '復原')}>
                  <IconButton size="small" color="primary" onClick={() => onRestore?.(fid)}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title={t('myDrive.preview', '預覽')}>
                  <IconButton size="small" onClick={() => preview(fid)}><VisibilityIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title={t('myCalendar.removeAttachment', '移除附件')}>
                  <IconButton size="small" color="error" onClick={() => onRemove(fid)}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      })}
      {/* 待儲存 picker（灰框虛線 + chip）*/}
      {pendingPicks.map((fid, idx) => {
        const m = metas.find(x => x.id === fid);
        return (
          <Box key={`pp-${fid}-${idx}`} display="flex" alignItems="center" gap={1}
               sx={{ px: 1, py: 0.5, border: '1px dashed', borderColor: 'grey.400', borderRadius: 1 }}>
            <InsertDriveFileIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>{m?.filename ?? `#${fid}`}</Typography>
            <Chip size="small" variant="outlined" label={t('common.pending', '待儲存')} />
            <IconButton size="small" color="error" onClick={() => onRemovePending?.(idx, 'pick')}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
      {/* 待儲存 upload（灰框虛線 + chip）*/}
      {pendingUploads.map((file, idx) => (
        <Box key={`pu-${idx}`} display="flex" alignItems="center" gap={1}
             sx={{ px: 1, py: 0.5, border: '1px dashed', borderColor: 'grey.400', borderRadius: 1 }}>
          <InsertDriveFileIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ flex: 1 }} noWrap>{file.name}</Typography>
          <Chip size="small" variant="outlined" label={t('common.pending', '待儲存')} />
          <IconButton size="small" color="error" onClick={() => onRemovePending?.(idx, 'upload')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

const EeMyCalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { showSuccess, showApiError, showWarning, showError } = useMessage();
  const { hasFeature } = useSystem();
  const { user } = useAuth();   // 目前登入者：助手 picker 排除自己（不能指定自己為助手）
  const hasInitialized = useRef(false);

  const [events, setEvents] = useState<EeMyCalendar[]>([]);
  const [loading, setLoading] = useState(false);
  const [calendarApiKey, setCalendarApiKey] = useState('');
  const [holidayCountries, setHolidayCountries] = useState<string[]>(['TW']);
  const [holidayColors, setHolidayColors] = useState<Record<string, string>>({ ...DEFAULT_HOLIDAY_COLORS });
  const [restDays, setRestDays] = useState<number[]>([0, 6]); // 0=日 6=六
  const [restDayHex, setRestDayHex] = useState('#4caf50');
  const [showControls, setShowControls] = useState(false);

  // 行事曆助手：身分切換（actingOwnerId=null 表操作本人行事曆）+ 我可幫誰 + 助手管理
  const [myScopes, setMyScopes] = useState<CalendarScope[]>([]);
  const [actingOwnerId, setActingOwnerId] = useState<number | null>(null);
  const [assistDialogOpen, setAssistDialogOpen] = useState(false);
  const [myAssistants, setMyAssistants] = useState<CalendarAssistant[]>([]);
  const [tenantUsers, setTenantUsers] = useState<MemberOption[]>([]);
  const [pickedAssistant, setPickedAssistant] = useState<MemberOption | null>(null);
  const actingScope = myScopes.find((sc) => sc.owner_user_id === actingOwnerId) || null;

  const openAssistDialog = async () => {
    setAssistDialogOpen(true);
    setPickedAssistant(null);
    try {
      const [as, us] = await Promise.all([listAssistants(), getMemberOptions().catch(() => [])]);
      setMyAssistants(as);
      setTenantUsers(us);
    } catch (err) { showApiError(err); }
  };

  const handleAddAssistant = async () => {
    if (!pickedAssistant) return;
    try {
      await addAssistant(pickedAssistant.id);
      showSuccess('SYS020001', { name: t('calendar.assistant.title', '行事曆助手') });
      setPickedAssistant(null);
      setMyAssistants(await listAssistants());
    } catch (err) { showApiError(err); }
  };

  const handleRevokeAssistant = async (recordId: number) => {
    try {
      await revokeAssistant(recordId);
      showSuccess('SYS020003', { name: t('calendar.assistant.title', '行事曆助手') });
      setMyAssistants(await listAssistants());
    } catch (err) { showApiError(err); }
  };

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EeMyCalendar | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<EeMyCalendar | null>(null);
  const [formData, setFormData] = useState<EeMyCalendarCreate>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // duration 自動同步：使用者未手動改 end 前，改 start 自動 +1h（porsche 2026-08-27）
  const [endManuallyEdited, setEndManuallyEdited] = useState(false);
  // 事件繁忙提醒（時間衝突）：儲存前 pre-check，衝突時彈確認框（promise 化）
  const [busyOpen, setBusyOpen] = useState(false);
  const [busyConflicts, setBusyConflicts] = useState<ConflictItem[]>([]);
  const busyResolveRef = useRef<((v: boolean) => void) | null>(null);
  // 附件
  const [attachmentFileIds, setAttachmentFileIds] = useState<number[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const attachUploadRef = useRef<HTMLInputElement>(null);
  // 附件本地緩衝：新增/編輯一律 buffer，按「儲存」才 commit backend（cancel 不殘留）
  const [pendingUploads, setPendingUploads] = useState<File[]>([]);
  const [pendingPicks, setPendingPicks]     = useState<number[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<number[]>([]);  // 待從 attachmentFileIds 移除的 file_id
  const [myLocations, setMyLocations] = useState<MyLocation[]>([]);
  // 出席者（從聯絡人挑選）
  const [contacts, setContacts] = useState<EeMyContact[]>([]);
  const [dialogAttendees, setDialogAttendees] = useState<EeMyContact[]>([]);
  const [notifying, setNotifying] = useState(false);

  // 會議整合（Stage 3·H.1）：組織成員選擇器 + 會議鎖定（已開始/結束不可改時間）+ 取消勾選確認
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [meetingMembers, setMeetingMembers] = useState<MemberOption[]>([]);
  const [freeBusy, setFreeBusy] = useState<Record<number, 'free' | 'busy' | 'tentative'>>({});
  const [meetingLocked, setMeetingLocked] = useState(false);   // 對應會議已開始/結束
  const [uncheckConfirmOpen, setUncheckConfirmOpen] = useState(false);
  // 假日 tag DOM 追蹤（eventDidMount 注入到 day-top，eventWillUnmount 清除）
  const holidayTagEls = useRef<Map<string, HTMLElement>>(new Map());

  const loadEvents = useCallback(async (start?: string, end?: string) => {
    try {
      setLoading(true);
      const data = await getEvents({ start, end, ownerUserId: actingOwnerId });
      setEvents(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError, actingOwnerId]);

  // 切換操作身分 → 重載該身分的行事曆
  useEffect(() => {
    if (hasInitialized.current) loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actingOwnerId]);

  useEffect(() => {
    if (!permissionLoading && hasPermission(FUNC_CODE, 'read') && !hasInitialized.current) {
      hasInitialized.current = true;
      loadEvents();
      getMyScopes().then(setMyScopes).catch(() => {});   // 我可幫哪些人管理行事曆
      // #19：getContacts 改回 envelope {total, items} → 取 items（含 200 上限，行事曆挑人夠用）
      getContacts({ limit: 200 }).then(r => setContacts(r.items)).catch(() => {});
      getMyLocations().then(setMyLocations).catch(() => {});
      getMemberOptions().then(setMemberOptions).catch(() => {});   // 會議出席者（組織成員選擇器·H.1）
      // 載入 Google Calendar API Key（E6b-c：改用登入者可讀的日曆 config 端點·非平台管理 settings）
      getMyCalendarConfig().then(c => {
        if (c?.calendar_api_key) setCalendarApiKey(c.calendar_api_key);
      }).catch(() => { /* silent ok: 節日 key 載入失敗不阻擋日曆 */ });
    }
  }, [permissionLoading, hasPermission, loadEvents]);

  // H.4：從會議室「建立會議」帶 ?new=meeting 進來 → 直接開會議表單（預設勾「這是會議」）
  useEffect(() => {
    if (searchParams.get('new') === 'meeting' && !permissionLoading && hasPermission(FUNC_CODE, 'create')) {
      setEditingEvent(null);
      setEndManuallyEdited(false);
      setFormData({ ...emptyForm(), is_meeting: true });
      setDialogAttendees([]);
      setMeetingMembers([]);
      setMeetingLocked(false);
      setAttachmentFileIds([]);
      setPendingUploads([]);
      setPendingPicks([]);
      setPendingRemovals([]);
      setDialogOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, permissionLoading, hasPermission, setSearchParams]);

  // H.2：會議表單依選定時段查成員忙碌狀態（debounce；衝突只提示，仍可邀請）
  useEffect(() => {
    if (!dialogOpen || !formData.is_meeting || !formData.dtstart) return;
    const ids = memberOptions.map(m => m.id).slice(0, 50);
    if (ids.length === 0) return;
    const start = formData.dtstart;
    const end = formData.dtend || new Date(new Date(formData.dtstart).getTime() + 3600_000).toISOString();
    const h = setTimeout(() => {
      getFreeBusy(ids, start, end).then(resp => {
        const map: Record<number, 'free' | 'busy' | 'tentative'> = {};
        ids.forEach(id => {
          const blocks = resp[id] || [];
          map[id] = blocks.some(b => b.status === 'busy') ? 'busy'
            : blocks.some(b => b.status === 'tentative') ? 'tentative' : 'free';
        });
        setFreeBusy(map);
      }).catch(() => { /* silent ok: 忙碌查詢失敗不阻擋邀請 */ });
    }, 400);
    return () => clearTimeout(h);
  }, [dialogOpen, formData.is_meeting, formData.dtstart, formData.dtend, memberOptions]);

  // Convert backend events to FullCalendar format
  const _tz = getDisplayTimeZone();
  const fcEvents: EventInput[] = events.map((ev) => {
    // H.3 會議行程標示：公開會議 📅、秘密會議 🔒（判斷改看 is_meeting，不看 event_type）
    const title = (ev.is_meeting ? (ev.public_meeting === false ? '🔒 ' : '📅 ') : '') + ev.summary;
    const common = {
      id: String(ev.id), title, allDay: ev.is_all_day,
      color: ev.color ?? '#1976d2', extendedProps: { raw: ev },
    };
    if (!ev.rrule) {
      return { ...common, start: ev.dtstart, end: ev.dtend ?? undefined };
    }
    // 有循環規則：plugin 忽略 start/end，必須在 rrule 字串內帶 DTSTART，另給 duration（不傳 end）。
    const hasDtstart = /DTSTART/i.test(ev.rrule);
    const durMs = ev.dtend ? Math.max(new Date(ev.dtend).getTime() - new Date(ev.dtstart).getTime(), 0) : 0;
    if (ev.is_all_day) {
      const days = durMs > 0 ? Math.round(durMs / 86_400_000) : 1;
      return {
        ...common,
        // K4：rrule@2.8.1 不認 `DTSTART;VALUE=DATE:` → 會忽略改用「現在」；改用純日期 `DTSTART:YYYYMMDD`
        rrule: hasDtstart ? ev.rrule : `DTSTART:${toICalFloatingDate(ev.dtstart, _tz)}\nRRULE:${ev.rrule}`,
        duration: { days: days > 0 ? days : 1 },
      };
    }
    return {
      ...common,
      rrule: hasDtstart ? ev.rrule : `DTSTART:${toICalFloating(ev.dtstart, _tz)}\nRRULE:${ev.rrule}`,
      duration: { milliseconds: durMs > 0 ? durMs : 3_600_000 },
    };
  });

  const handleDateClick = (arg: DateClickArg) => {
    if (!hasPermission(FUNC_CODE, 'create')) return;
    // hotfix 2026-10-24：點哪天就從那天開始（不再一律用今天）。
    // - 月檢視（allDay）：點選的那天 + 現在的時:分（租戶時區，不用 00:00）。
    // - 週/日檢視某時段：用該時段（arg.date 已是絕對時刻）。
    // - 「新增事件」按鈕 / ?new=meeting 仍用現在（走 emptyForm，不經本函式）。
    const tz = getDisplayTimeZone();
    let startIso: string;
    if (arg.allDay) {
      const nowHm = toDateTimeLocalValue(new Date().toISOString(), tz).split('T')[1];  // 現在的 HH:mm（租戶時區）
      startIso = dateTimeLocalToUtcIso(`${arg.dateStr}T${nowHm}`, tz);
    } else {
      startIso = arg.date.toISOString();
    }
    setEndManuallyEdited(false);
    setEditingEvent(null);
    setFormData({
      ...emptyForm(),
      dtstart: startIso,
      dtend: new Date(new Date(startIso).getTime() + 3_600_000).toISOString(),
    });
    setDialogAttendees([]);
    setMeetingMembers([]);
    setMeetingLocked(false);
    setFreeBusy({});
    setAttachmentFileIds([]);
    setPendingUploads([]);
    setPendingPicks([]);
    setPendingRemovals([]);
    setDialogOpen(true);
  };

  const handleEventClick = (arg: EventClickArg) => {
    const raw = arg.event.extendedProps.raw as EeMyCalendar;
    if (!raw) return;
    if (hasPermission(FUNC_CODE, 'update')) {
      setEditingEvent(raw);
      setEndManuallyEdited(true);   // 既有事件已有自己的 end，不自動覆蓋
      setFormData({
        summary: raw.summary,
        description: raw.description ?? '',
        location: raw.location ?? '',
        dtstart: raw.dtstart,
        dtend: raw.dtend ?? undefined,
        is_all_day: raw.is_all_day,
        rrule: raw.rrule ?? '',
        status: raw.status,
        color: raw.color ?? COLORS[0],
        is_active: raw.is_active,
        is_meeting: !!raw.is_meeting,
        public_meeting: raw.public_meeting !== false,
      });
      // 還原已選出席者：會議→組織成員（依 user_id）；一般事件→聯絡人（依 contact_id）
      if (raw.is_meeting) {
        const mm = (raw.attendees ?? [])
          .filter(a => a.user_id != null)
          .map(a => memberOptions.find(m => m.id === a.user_id)
            ?? { id: a.user_id as number, username: a.name ?? `#${a.user_id}`, account: '' })
          .filter(Boolean) as MemberOption[];
        setMeetingMembers(mm);
        setDialogAttendees([]);
      } else {
        const restored = (raw.attendees ?? [])
          .filter(a => a.contact_id)
          .map(a => contacts.find(c => c.id === a.contact_id))
          .filter(Boolean) as EeMyContact[];
        setDialogAttendees(restored);
        setMeetingMembers([]);
      }
      // 會議已開始/結束 → 時間欄位不可修改（查會議狀態）
      setMeetingLocked(false);
      if (raw.is_meeting && raw.office_meeting_id) {
        getMeeting(raw.office_meeting_id)
          .then(m => setMeetingLocked(!!m && m.status !== 'scheduled' && m.status !== 'cancelled'))
          .catch(() => { /* silent ok: 查不到狀態不鎖，後端仍以 409 把關 */ });
      }
      setAttachmentFileIds(raw.attachment_file_ids ?? []);
      setPendingUploads([]);
      setPendingPicks([]);
      setPendingRemovals([]);
      setDialogOpen(true);
    }
  };

  const handleEventDrop = async (arg: EventDropArg) => {
    if (!hasPermission(FUNC_CODE, 'update')) {
      arg.revert();
      return;
    }
    const raw = arg.event.extendedProps.raw as EeMyCalendar;
    if (!raw) { arg.revert(); return; }
    try {
      const payload: EeMyCalendarUpdate = {
        dtstart: arg.event.start?.toISOString(),
        dtend: arg.event.end?.toISOString(),
      };
      await updateEvent(raw.id, payload, actingOwnerId);
      showSuccess('SYS310042', { name: raw.summary });
      loadEvents();
    } catch (err) {
      arg.revert();
      showApiError(err);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingEvent(null);
    setFormData(emptyForm());
    setDialogAttendees([]);
    setMeetingMembers([]);
    setMeetingLocked(false);
    setFreeBusy({});
    setAttachmentFileIds([]);
    setPendingUploads([]);
    setPendingPicks([]);
    setPendingRemovals([]);
  };

  // ── 附件 handlers：一律本地緩衝（新增/編輯皆同），按「儲存」才 commit backend ──
  const handleAttachUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // 清掉讓同檔可重選
    if (!file) return;
    setPendingUploads(prev => [...prev, file]);   // 一律 buffer，儲存才 commit
  };

  const handleAttachPick = async (fileIds: number[]) => {
    setPickerOpen(false);
    setPendingPicks(prev => [
      ...prev,
      ...fileIds.filter(id => !prev.includes(id) && !attachmentFileIds.includes(id)),
    ]);
  };

  const removePending = (idx: number, type: 'upload' | 'pick') => {
    if (type === 'upload') setPendingUploads(prev => prev.filter((_, i) => i !== idx));
    else setPendingPicks(prev => prev.filter((_, i) => i !== idx));
  };

  // 標記移除既有 ref（不打 backend，儲存才 commit）；若是這次 dialog 內待加的 pick → 直接拔掉
  const handleAttachRemove = (fileId: number) => {
    if (attachmentFileIds.includes(fileId)) {
      setPendingRemovals(prev => prev.includes(fileId) ? prev : [...prev, fileId]);
      return;
    }
    setPendingPicks(prev => prev.filter(x => x !== fileId));
  };

  const handleAttachRestore = (fileId: number) => {
    setPendingRemovals(prev => prev.filter(x => x !== fileId));
  };

  // 儲存後 commit 三個 buffer：先 removals（騰出 quota）→ uploads → picks
  const commitPendingAttachments = async (eventId: number) => {
    const failures: string[] = [];
    for (const fid of pendingRemovals) {
      try { await removeAttachment(eventId, fid); }
      catch { failures.push(`remove#${fid}`); }
    }
    for (const file of pendingUploads) {
      try { await uploadAttachment(eventId, file); }
      catch { failures.push(file.name); }
    }
    for (const fid of pendingPicks) {
      try { await pickAttachment(eventId, fid); }
      catch { failures.push(`pick#${fid}`); }
    }
    if (failures.length > 0) {
      showWarning('SYS020009', { detail: failures.join(', ') });
    }
  };

  const buildEventPayload = () => {
    // 會議：出席者只送 user_id（name/email 由後端補）；一般事件：維持聯絡人格式
    const attendees = formData.is_meeting
      ? meetingMembers.map(m => ({ user_id: m.id }))
      : dialogAttendees
          .map(c => ({ contact_id: c.id, name: c.display_name, email: c.emails?.[0]?.value ?? '' }))
          .filter(a => a.email);
    const payload = { ...formData, attendees };
    if (!payload.rrule) delete (payload as Partial<typeof payload>).rrule;
    if (!payload.dtend) delete (payload as Partial<typeof payload>).dtend;
    return payload;
  };

  // H.1「這是會議」切換：開啟時若已有外部聯絡人出席者 → 擋下並列出要移除的人；
  // 關閉既有會議 → 先確認（會議將取消）。用頁面內 Dialog，不用 confirm()。
  const handleToggleMeeting = (checked: boolean) => {
    if (checked) {
      if (dialogAttendees.length > 0) {
        showWarning('ERR310004', { names: dialogAttendees.map(c => c.display_name).join('、') });
        return;   // 請使用者先移除聯絡人，再勾「這是會議」
      }
      setFormData(prev => ({ ...prev, is_meeting: true }));
      return;
    }
    // 取消勾選
    if (editingEvent?.is_meeting) {
      setUncheckConfirmOpen(true);   // 既有會議 → 確認後才取消
    } else {
      setFormData(prev => ({ ...prev, is_meeting: false }));
      setMeetingMembers([]);
    }
  };

  const confirmUncheckMeeting = () => {
    setUncheckConfirmOpen(false);
    setFormData(prev => ({ ...prev, is_meeting: false }));
    setMeetingMembers([]);
  };

  const fmtConflict = (cf: ConflictItem) => {
    const s = new Date(cf.dtstart).toLocaleString();
    const e = cf.dtend ? new Date(cf.dtend).toLocaleString() : '';
    return e ? `${s} ~ ${e}` : s;
  };

  // 儲存前檢查時間衝突：無衝突→直接存；有衝突→彈「事件繁忙提醒」，確認後才 force 存
  const preCheckConflict = async (): Promise<{ proceed: boolean; force: boolean }> => {
    try {
      const res = await checkConflict({
        dtstart: formData.dtstart,
        dtend: formData.dtend ?? null,
        is_all_day: formData.is_all_day,
        exclude_event_id: editingEvent?.id ?? null,
      }, actingOwnerId);
      if (!res.has_conflict) return { proceed: true, force: false };
      setBusyConflicts(res.conflicts);
      setBusyOpen(true);
      const confirmed = await new Promise<boolean>((resolve) => { busyResolveRef.current = resolve; });
      setBusyOpen(false);
      busyResolveRef.current = null;
      return confirmed ? { proceed: true, force: true } : { proceed: false, force: false };
    } catch {
      // silent ok: pre-check 失敗不阻擋，交後端 force=false guard（有衝突會回 409 → showApiError）
      return { proceed: true, force: false };
    }
  };

  // Stage 6 前：會議 + 循環擋下（後端 ERR020006 也擋；此處先給即時提示）
  const meetingRecurringBlocked = (): boolean => {
    if (formData.is_meeting && (formData.rrule || '').trim()) {
      showError('ERR020006', {
        field: t('calendar.rrule', '循環設定'),
        detail: t('calendar.meetingNoRecurring', '循環會議即將開放，目前請逐次建立會議'),
      });
      return true;
    }
    return false;
  };

  const handleSave = async () => {
    if (!formData.summary.trim()) return;
    if (meetingRecurringBlocked()) return;
    setSaving(true);
    try {
      const gate = await preCheckConflict();
      if (!gate.proceed) return;   // 使用者於繁忙提醒選「取消」→ finally 復位 saving
      const payload = buildEventPayload();
      let eventId: number;
      if (editingEvent) {
        await updateEvent(editingEvent.id, payload as EeMyCalendarUpdate, actingOwnerId, gate.force);
        eventId = editingEvent.id;
        showSuccess('SYS310042', { name: formData.summary });
      } else {
        const created = await createEvent(payload as EeMyCalendarCreate, actingOwnerId, gate.force);
        eventId = created.id;
        showSuccess('SYS310041', { name: formData.summary });
      }
      await commitPendingAttachments(eventId);   // 一律 commit 緩衝附件（新增/編輯皆同）
      handleDialogClose();
      loadEvents();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNotify = async () => {
    if (!formData.summary.trim()) return;
    if (meetingRecurringBlocked()) return;
    setSaving(true);
    try {
      const gate = await preCheckConflict();
      if (!gate.proceed) return;
      const payload = buildEventPayload();
      let eventId: number;
      if (editingEvent) {
        await updateEvent(editingEvent.id, payload as EeMyCalendarUpdate, actingOwnerId, gate.force);
        eventId = editingEvent.id;
      } else {
        const created = await createEvent(payload as EeMyCalendarCreate, actingOwnerId, gate.force);
        eventId = created.id;
      }
      await commitPendingAttachments(eventId);   // 一律 commit 緩衝附件（新增/編輯皆同）
      setNotifying(true);
      try {
        const result = await notifyEvent(eventId);
        showSuccess('SYS310041', {
          name: `通知已傳送 ${result.sent} 封${result.skipped > 0 ? `，失敗 ${result.skipped}` : ''}`,
        });
      } catch (notifyErr) {
        showApiError(notifyErr);
      } finally {
        setNotifying(false);
      }
      handleDialogClose();
      loadEvents();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (ev: EeMyCalendar) => {
    setDeletingEvent(ev);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingEvent) return;
    setDeleting(true);
    try {
      await deleteEvent(deletingEvent.id, actingOwnerId);
      showSuccess('SYS310043', { name: deletingEvent.summary });
      setDeleteDialogOpen(false);
      setDeletingEvent(null);
      loadEvents();
    } catch (err) {
      showApiError(err);
    } finally {
      setDeleting(false);
    }
  };

  // 假日每國顏色 CSS（動態注入，供 style 標籤使用）
  // 需同時覆蓋 FullCalendar CSS 變數 --fc-event-text-color（否則文字仍白色）
  const holidayColorCss = Object.entries(holidayColors)
    .map(([code, color]) => [
      `.fc-event.holiday-${code} { --fc-event-text-color: ${color}; border: 1.5px solid ${color} !important; color: ${color} !important; }`,
      `.fc-event.holiday-${code} .fc-event-title { color: ${color} !important; }`,
    ].join('\n          '))
    .join('\n          ');

  if (permissionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasPermission(FUNC_CODE, 'read')) {
    return (
      <Box p={3}>
        <Alert severity="warning">{t('common.noPermission', '無此功能存取權限')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FunctionPageHeader funcCode={FUNC_CODE} />

      {/* 操作身分切換（我可幫誰管理行事曆）· stateless per-request owner */}
      {myScopes.length > 0 && (
        <Box display="flex" gap={1} mb={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('calendar.assistant.actingLabel', '操作身分：')}
          </Typography>
          <Chip label={t('calendar.assistant.self', '我自己')} size="small"
            color={actingOwnerId === null ? 'primary' : 'default'}
            variant={actingOwnerId === null ? 'filled' : 'outlined'}
            onClick={() => setActingOwnerId(null)} />
          {myScopes.map((sc) => (
            <Chip key={sc.owner_user_id} size="small"
              label={`${sc.owner_username || sc.owner_account}（${t('calendar.assistant.helping', '幫忙')}）`}
              color={actingOwnerId === sc.owner_user_id ? 'primary' : 'default'}
              variant={actingOwnerId === sc.owner_user_id ? 'filled' : 'outlined'}
              onClick={() => setActingOwnerId(sc.owner_user_id)} />
          ))}
        </Box>
      )}
      {actingScope && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('calendar.assistant.actingBanner', {
            defaultValue: '目前正在幫「{{name}}」管理行事曆，所有新增／修改都會記在對方名下。',
            name: actingScope.owner_username || actingScope.owner_account || '',
          })}
        </Alert>
      )}

      <Box display="flex" gap={1} mb={1} alignItems="center">
        {hasPermission(FUNC_CODE, 'create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditingEvent(null); setEndManuallyEdited(false); setFormData(emptyForm()); setDialogAttendees([]); setMeetingMembers([]); setMeetingLocked(false); setAttachmentFileIds([]); setPendingUploads([]); setPendingPicks([]); setPendingRemovals([]); setDialogOpen(true); }}
          >
            {t('calendar.add', '新增事件')}
          </Button>
        )}
        {actingOwnerId === null && (
          <Button size="small" variant="outlined" onClick={openAssistDialog}>
            {t('calendar.assistant.manage', '行事曆助手')}
          </Button>
        )}
        <Tooltip title={showControls ? '收合顯示設定' : '展開顯示設定'}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon fontSize="small" />}
            endIcon={<ExpandMoreIcon fontSize="small" sx={{ transform: showControls ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
            onClick={() => setShowControls(v => !v)}
          >
            顯示設定
          </Button>
        </Tooltip>
        {loading && <CircularProgress size={20} />}
      </Box>

      <Collapse in={showControls}>
        <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap"
             sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
          {/* 國定假日 */}
          {calendarApiKey && (
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <Typography variant="body2" sx={{ mr: 0.5 }}>國定假日：</Typography>
              {HOLIDAY_META.map(([code, label]) => {
                const enabled = holidayCountries.includes(code);
                const color = holidayColors[code] ?? DEFAULT_HOLIDAY_COLORS[code];
                return (
                  <Box key={code} display="flex" alignItems="center"
                    sx={{ border: `1.5px solid ${enabled ? color : '#ddd'}`, borderRadius: 1, px: 0.5, gap: 0.25, transition: 'border-color 0.2s' }}>
                    <Checkbox
                      checked={enabled}
                      onChange={e => setHolidayCountries(prev =>
                        e.target.checked ? [...prev, code] : prev.filter(c => c !== code)
                      )}
                      size="small"
                      sx={{ p: 0.3, color: enabled ? color : undefined, '&.Mui-checked': { color } }}
                    />
                    <Typography variant="body2">{label}</Typography>
                    <input
                      type="color"
                      value={color}
                      onChange={e => setHolidayColors(prev => ({ ...prev, [code]: e.target.value }))}
                      title={`${label} 假日顏色`}
                      style={{ width: 18, height: 18, padding: 1, border: '1px solid #ccc', borderRadius: 3, cursor: 'pointer', marginLeft: 2 }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
          {/* 休假日標色 */}
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <Typography variant="body2" sx={{ mr: 0.5 }}>休假日：</Typography>
            {(['日','一','二','三','四','五','六'] as const).map((label, idx) => (
              <Box key={idx} display="flex" alignItems="center" sx={{ border: '1px solid #ddd', borderRadius: 1, px: 0.5 }}>
                <Checkbox
                  checked={restDays.includes(idx)}
                  onChange={e => setRestDays(prev => e.target.checked ? [...prev, idx] : prev.filter(d => d !== idx))}
                  size="small"
                  sx={{ p: 0.3 }}
                />
                <Typography variant="body2">週{label}</Typography>
              </Box>
            ))}
            {restDays.length > 0 && (
              <input
                type="color"
                value={restDayHex}
                onChange={e => setRestDayHex(e.target.value)}
                title="選擇休假日背景色"
                style={{ width: 24, height: 24, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
              />
            )}
          </Box>
        </Box>
      </Collapse>

      <style>{`
        .fc-event-location {
          display: block; font-size: 9px; opacity: 0.9;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          cursor: pointer; text-decoration: underline; padding-left: 2px;
        }
        .fc-event-location:hover { opacity: 1; font-weight: 600; }
      `}</style>
      {restDays.length > 0 && (
        <style>{`.fc-restday-hl { background-color: ${restDayHex}26 !important; }`}</style>
      )}
      {calendarApiKey && holidayCountries.length > 0 && (
        <style>{`
          .fc-event.holiday-tag { display: none !important; }
          .fc-daygrid-day-top { gap: 2px; align-items: center; }
          .fc-daygrid-day-top .holiday-day-tag {
            flex: 1; min-width: 0;
            font-size: 10px; font-weight: 600;
            border-radius: 3px; padding: 0 4px; line-height: 16px;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
            cursor: default;
          }
        `}</style>
      )}

      <FullCalendar
        key={calendarApiKey ? 'fc-with-google' : 'fc-no-google'}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin, googleCalendarPlugin]}
        googleCalendarApiKey={calendarApiKey || undefined}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        locales={[zhTwLocale, enLocale]}
        locale={i18n.language === 'en' ? 'en-gb' : 'zh-tw'}
        eventSources={[
          { events: fcEvents },
          ...(calendarApiKey
            ? holidayCountries
                .filter(c => HOLIDAY_CALENDARS[c])
                .map(c => ({
                  googleCalendarId: HOLIDAY_CALENDARS[c],
                  classNames: ['holiday-tag', `holiday-${c}`],
                }))
            : []),
        ]}
        dayCellClassNames={(arg) => restDays.includes(arg.date.getDay()) ? ['fc-restday-hl'] : []}
        eventOrder={(a: any, b: any) => {
          const aH = (a.classNames ?? []).includes('holiday-tag') ? -1 : 0;
          const bH = (b.classNames ?? []).includes('holiday-tag') ? -1 : 0;
          if (aH !== bH) return aH - bH;
          return (a.start?.valueOf() ?? 0) - (b.start?.valueOf() ?? 0);
        }}
        dayMaxEventRows={false}
        editable={hasPermission(FUNC_CODE, 'update')}
        selectable={hasPermission(FUNC_CODE, 'create')}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        eventDrop={handleEventDrop}
        eventDidMount={(arg) => {
          const elClasses = Array.from(arg.el.classList);
          if (elClasses.includes('holiday-tag')) {
            // ── inject holiday tag into day-top row ──
            const countryCode = elClasses
              .find((c: string) => c.startsWith('holiday-') && c !== 'holiday-tag')
              ?.replace('holiday-', '');
            const color = holidayColors[countryCode ?? ''] ?? DEFAULT_HOLIDAY_COLORS[countryCode ?? ''] ?? '#ff6f00';
            const dayTop = arg.el.closest('.fc-daygrid-day')?.querySelector('.fc-daygrid-day-top') as HTMLElement | null;
            if (!dayTop) return;
            const key = `${arg.event.startStr}-${countryCode ?? ''}-${arg.event.title}`;
            if (holidayTagEls.current.has(key)) return;
            const tag = document.createElement('span');
            tag.className = 'holiday-day-tag';
            tag.textContent = arg.event.title;
            tag.style.color = color;
            tag.style.border = `1.5px solid ${color}`;
            tag.style.backgroundColor = 'white';
            holidayTagEls.current.set(key, tag);
            dayTop.appendChild(tag);
            return;
          }
          // ── inject clickable location for regular events ──
          const raw = (arg.event.extendedProps as { raw?: EeMyCalendar }).raw;
          if (!raw?.location) return;
          const locSpan = document.createElement('span');
          locSpan.className = 'fc-event-location';
          locSpan.textContent = `📍 ${raw.location}`;
          locSpan.addEventListener('click', (e) => {
            e.stopPropagation();
            navigateRef.current(`/ee_life_center?q=${encodeURIComponent(raw.location!)}`);
          });
          const mainFrame = arg.el.querySelector('.fc-event-main') ?? arg.el;
          mainFrame.appendChild(locSpan);
        }}
        eventWillUnmount={(arg) => {
          const elClasses = Array.from(arg.el.classList);
          if (!elClasses.includes('holiday-tag')) return;
          const countryCode = elClasses
            .find((c: string) => c.startsWith('holiday-') && c !== 'holiday-tag')
            ?.replace('holiday-', '');
          const key = `${arg.event.startStr}-${countryCode ?? ''}-${arg.event.title}`;
          const tag = holidayTagEls.current.get(key);
          if (tag) { tag.remove(); holidayTagEls.current.delete(key); }
        }}
        height="auto"
      />

      {/* 新增/編輯 Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEvent
            ? t('calendar.editTitle', '編輯事件')
            : t('calendar.addTitle', '新增事件')}
          {editingEvent?.acted_by_user_id != null
            && editingEvent.acted_by_user_id !== editingEvent.user_id && (
            <Chip size="small" color="info" variant="outlined" sx={{ ml: 1 }}
              label={t('calendar.assistant.actedTag', '助手代建')} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label={t('calendar.summary', '事件標題')}
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              required
              fullWidth
              size="small"
            />
            <TextField
              label={t('calendar.dtstart', '開始時間')}
              disabled={meetingLocked}
              type={formData.is_all_day ? 'date' : 'datetime-local'}
              value={formData.is_all_day
                ? formData.dtstart?.slice(0, 10)
                : toLocalDatetime(formData.dtstart || '')}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                // 定時：輸入的牆上時間以租戶時區反解回 UTC；全天：純日期當 UTC 午夜
                const startIso = formData.is_all_day
                  ? new Date(`${v}T00:00:00.000Z`).toISOString()
                  : dateTimeLocalToUtcIso(v);
                setFormData((prev) => {
                  const next = { ...prev, dtstart: startIso };
                  // 使用者尚未手動改 end 且非全天 → end 自動同步為 start + 1h
                  if (!endManuallyEdited && !prev.is_all_day) {
                    next.dtend = new Date(new Date(startIso).getTime() + 3600_000).toISOString();
                  }
                  return next;
                });
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('calendar.dtend', '結束時間')}
              disabled={meetingLocked}
              helperText={meetingLocked ? t('calendar.meetingLockedHint', '會議已開始或結束，時間不可修改') : undefined}
              type={formData.is_all_day ? 'date' : 'datetime-local'}
              value={formData.dtend
                ? formData.is_all_day
                  ? formData.dtend.slice(0, 10)
                  : toLocalDatetime(formData.dtend)
                : ''}
              onChange={(e) => {
                setEndManuallyEdited(true);   // 使用者手動改 end → 停止 auto-sync
                const v = e.target.value;
                const endIso = v
                  ? (formData.is_all_day ? new Date(`${v}T00:00:00.000Z`).toISOString() : dateTimeLocalToUtcIso(v))
                  : undefined;
                setFormData({ ...formData, dtend: endIso });
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              freeSolo
              options={myLocations.map(l => l.name)}
              value={formData.location ?? ''}
              onInputChange={(_e, val) => setFormData({ ...formData, location: val })}
              onChange={(_e, val) => setFormData({ ...formData, location: val ?? '' })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('calendar.location', '地點')}
                  size="small"
                  fullWidth
                />
              )}
            />
            <RRuleBuilder
              value={formData.rrule ?? ''}
              onChange={(rrule) => setFormData({ ...formData, rrule })}
            />
            <TextField
              label={t('calendar.description', '說明')}
              value={formData.description ?? ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            {/* 這是會議（H.1）：勾選後出現公開會議 + 組織成員選擇器，取代聯絡人出席者 */}
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={!!formData.is_meeting}
                  disabled={meetingLocked}
                  onChange={(e) => handleToggleMeeting(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  {t('calendar.isMeeting', '這是會議（建立會議室並邀請與會者）')}
                  {meetingLocked && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {t('calendar.meetingLockedHint', '會議已開始或結束，時間不可修改')}
                    </Typography>
                  )}
                </Typography>
              }
            />

            {formData.is_meeting ? (
              <>
                <Box>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.public_meeting !== false}
                        onChange={(e) => setFormData({ ...formData, public_meeting: e.target.checked })}
                      />
                    }
                    label={<Typography variant="body2">{t('calendar.publicMeeting', '公開會議')}</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 3.5 }}>
                    {t('calendar.secretMeetingHint', '取消勾選 = 秘密會議，只有與會者看得到')}
                  </Typography>
                </Box>
                <Autocomplete
                  multiple
                  options={memberOptions}
                  getOptionLabel={m => `${m.username}${m.account ? ` (${m.account})` : ''}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={meetingMembers}
                  onChange={(_, selected) => setMeetingMembers(selected as MemberOption[])}
                  renderInput={params => (
                    <TextField {...params} label={t('calendar.meetingAttendees', '出席者（組織成員）')}
                      size="small" placeholder={t('calendar.searchName', '搜尋姓名...') as string} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((m, index) => (
                      <Chip label={m.username} size="small" {...getTagProps({ index })} key={m.id} />
                    ))
                  }
                  renderOption={(props, m) => {
                    const st = freeBusy[m.id];
                    const label = st === 'busy' ? t('calendar.busy', '忙碌')
                      : st === 'tentative' ? t('calendar.tentative', '暫定')
                      : st === 'free' ? t('calendar.free', '有空') : '';
                    const color: 'error' | 'warning' | 'success' = st === 'busy' ? 'error'
                      : st === 'tentative' ? 'warning' : 'success';
                    return (
                      <li {...props} key={m.id}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Typography variant="body2">{m.username}{m.account ? ` (${m.account})` : ''}</Typography>
                          {label && <Chip size="small" color={color} variant="outlined" label={label} />}
                        </Box>
                      </li>
                    );
                  }}
                  noOptionsText={t('calendar.noMembers', '無符合組織成員')}
                  size="small"
                  fullWidth
                />
              </>
            ) : (
              <Autocomplete
                multiple
                options={contacts.filter(c => (c.emails?.length ?? 0) > 0)}
                getOptionLabel={c => `${c.display_name}${c.org ? ` (${c.org})` : ''}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={dialogAttendees}
                onChange={(_, selected) => setDialogAttendees(selected as EeMyContact[])}
                renderInput={params => (
                  <TextField {...params} label="出席者（從聯絡人挑選）" size="small" placeholder="搜尋姓名..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((c, index) => (
                    <Chip label={c.display_name} size="small" {...getTagProps({ index })} key={c.id} />
                  ))
                }
                renderOption={(props, c) => (
                  <li {...props} key={c.id}>
                    <Box>
                      <Typography variant="body2">{c.display_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.emails?.[0]?.value}{c.org ? ` · ${c.org}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="無符合聯絡人（需有 Email）"
                size="small"
                fullWidth
              />
            )}

            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                <Typography variant="caption" color="text.secondary">
                  {t('calendar.color', '顯示顏色')}
                </Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.is_all_day}
                      onChange={(e) => setFormData({ ...formData, is_all_day: e.target.checked })}
                    />
                  }
                  label={<Typography variant="caption">{t('calendar.allDay', '全天事件')}</Typography>}
                />
              </Box>
              <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap">
                {COLORS.map(c => {
                  const selected = (formData.color ?? COLORS[0]) === c;
                  return (
                    <Tooltip key={c} title={c}>
                      <Box
                        onClick={() => setFormData({ ...formData, color: c })}
                        sx={{
                          width: 26, height: 26, borderRadius: '50%', bgcolor: c,
                          cursor: 'pointer', flexShrink: 0,
                          boxShadow: selected
                            ? `0 0 0 2px white, 0 0 0 4px ${c}`
                            : '0 0 0 1px rgba(0,0,0,0.15)',
                          transform: selected ? 'scale(1.15)' : 'scale(1)',
                          transition: 'transform 0.15s, box-shadow 0.15s',
                          '&:hover': { transform: 'scale(1.2)' },
                        }}
                      />
                    </Tooltip>
                  );
                })}
                {/* 自訂顏色 */}
                <Tooltip title="自訂顏色">
                  <Box sx={{ position: 'relative', width: 26, height: 26, flexShrink: 0 }}>
                    <Box sx={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                    }} />
                    <input
                      type="color"
                      value={formData.color ?? COLORS[0]}
                      onChange={e => setFormData({ ...formData, color: e.target.value })}
                      style={{
                        position: 'absolute', inset: 0,
                        width: '100%', height: '100%',
                        opacity: 0, cursor: 'pointer', border: 'none', padding: 0,
                      }}
                    />
                  </Box>
                </Tooltip>
                {/* 目前選取色預覽 */}
                <Box sx={{
                  width: 26, height: 26, borderRadius: 1, bgcolor: formData.color ?? COLORS[0],
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.15)', flexShrink: 0,
                }} />
              </Box>
            </Box>

            {/* 附件區（新增事件也可加：本地緩衝，儲存後 flush）*/}
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                📎 {t('myCalendar.attachments', '附件')}
                {(pendingUploads.length + pendingPicks.length + pendingRemovals.length > 0) && (
                  <Box component="span" sx={{ ml: 1, fontSize: 12, color: 'info.main' }}>
                    ({pendingUploads.length + pendingPicks.length + pendingRemovals.length} {t('myCalendar.willSaveOnSubmit', '個待儲存')})
                  </Box>
                )}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" startIcon={<UploadFileIcon />}
                        onClick={() => attachUploadRef.current?.click()}>
                  {t('myCalendar.uploadAttachment', '上傳檔案')}
                </Button>
                <Button size="small" startIcon={<CloudIcon />}
                        onClick={() => setPickerOpen(true)}>
                  {t('myCalendar.pickFromMyDrive', '從我的網路硬碟')}
                </Button>
                <input ref={attachUploadRef} type="file" hidden onChange={handleAttachUpload} />
              </Stack>
              <AttachmentList
                fileIds={attachmentFileIds}
                pendingUploads={pendingUploads}
                pendingPicks={pendingPicks}
                pendingRemovals={pendingRemovals}
                onRemove={handleAttachRemove}
                onRestore={handleAttachRestore}
                onRemovePending={removePending}
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          {/* 編輯既有事件才顯示刪除 — 左側，跟取消/儲存分開 */}
          {editingEvent && hasPermission(FUNC_CODE, 'delete') && (
            <Button
              startIcon={<DeleteIcon />}
              color="error"
              onClick={() => {
                handleDialogClose();
                handleOpenDelete(editingEvent);
              }}
              disabled={saving || notifying || meetingLocked}
              sx={{ mr: 'auto' }}
            >
              {t('calendar.deleteEvent', '刪除事件')}
            </Button>
          )}
          {/* 會議行程：進入公司會議室（會議明細）*/}
          {editingEvent?.is_meeting && editingEvent.office_meeting_id && (
            <Button
              variant="outlined"
              onClick={() => {
                const mid = editingEvent.office_meeting_id;
                handleDialogClose();
                navigate(`/ee_office_meeting/${mid}`);
              }}
              disabled={saving || notifying}
            >
              {t('calendar.enterMeetingRoom', '進入會議室')}
            </Button>
          )}
          <Button onClick={handleDialogClose} disabled={saving || notifying}>
            {t('common.cancel', '取消')}
          </Button>
          {/* 再次通知：只編輯既有事件 + 有出席者 emails 才顯示（不儲存修改，只重發）*/}
          {editingEvent && dialogAttendees.filter(c => (c.emails?.length ?? 0) > 0).length > 0 && (
            <Tooltip title={t('myCalendar.renotifyTooltip', '寄送同一份邀請給出席者（不儲存當前修改）')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={notifying ? <CircularProgress size={14} /> : <EmailIcon />}
                  onClick={async () => {
                    try {
                      setNotifying(true);
                      await notifyEvent(editingEvent.id);
                      showSuccess('SYS020010');
                    } catch (err) {
                      showApiError(err);
                    } finally {
                      setNotifying(false);
                    }
                  }}
                  disabled={saving || notifying}
                >
                  {t('myCalendar.renotify', '再次通知')}
                </Button>
              </span>
            </Tooltip>
          )}
          {dialogAttendees.filter(c => (c.emails?.length ?? 0) > 0).length > 0 && (
            <Tooltip title="儲存後立即寄發邀請通知給出席者（需設定 SMTP）">
              <span>
                <Button
                  variant="outlined"
                  startIcon={notifying ? <CircularProgress size={14} /> : <EmailIcon />}
                  onClick={handleSaveAndNotify}
                  disabled={saving || notifying || !formData.summary.trim()}
                >
                  儲存並通知
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.summary.trim()}
          >
            {saving ? <CircularProgress size={16} /> : t('common.save', '儲存')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 從 my_drive 選附件 */}
      <FilePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handleAttachPick}
        multiSelect
      />

      {/* 刪除確認 Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>{t('calendar.deleteTitle', '確認刪除')}</DialogTitle>
        <DialogContent>
          <Typography>
            {deletingEvent?.is_meeting
              ? t('calendar.deleteMeetingConfirm', '確定要刪除會議行程「{{name}}」嗎？刪除後會議將取消。', {
                  name: deletingEvent?.summary,
                })
              : t('calendar.deleteConfirm', '確定要刪除事件「{{name}}」嗎？此操作無法復原。', {
                  name: deletingEvent?.summary,
                })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t('common.cancel', '取消')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={16} /> : t('common.delete', '刪除')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* H.1 取消勾選「這是會議」確認（會議將取消）*/}
      <Dialog open={uncheckConfirmOpen} onClose={() => setUncheckConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>{t('calendar.uncheckMeetingTitle', '取消會議')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('calendar.uncheckMeetingConfirm', '取消勾選後會議將取消，確定嗎？')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUncheckConfirmOpen(false)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" color="error" onClick={confirmUncheckMeeting}>
            {t('common.confirm', '確定')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 行事曆助手管理（owner 指定/撤銷，上限 2）*/}
      <Dialog open={assistDialogOpen} onClose={() => setAssistDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t('calendar.assistant.title', '行事曆助手')}（{myAssistants.length}/2）
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('calendar.assistant.hint', '被指定的助手可切換身分、代你維護行事曆（最多 2 位）。')}
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {myAssistants.map((a) => (
              <Box key={a.id} display="flex" alignItems="center" justifyContent="space-between"
                sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2">{a.assistant_username || a.assistant_account}</Typography>
                <Button size="small" color="error" onClick={() => handleRevokeAssistant(a.id)}>
                  {t('calendar.assistant.revoke', '撤銷')}
                </Button>
              </Box>
            ))}
            {myAssistants.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('calendar.assistant.none', '尚未指定助手')}
              </Typography>
            )}
          </Stack>
          {myAssistants.length < 2 && (
            <Box display="flex" gap={1} alignItems="center">
              <Autocomplete
                size="small" sx={{ flex: 1 }}
                options={tenantUsers.filter((u) =>
                  u.id !== user?.id &&   // 排除自己（後端也擋 self，UI 先不顯示）
                  !myAssistants.some((a) => a.assistant_user_id === u.id))}
                getOptionLabel={(u) => u.username || u.account || String(u.id)}
                value={pickedAssistant}
                onChange={(_, v) => setPickedAssistant(v)}
                renderInput={(params) => (
                  <TextField {...params} label={t('calendar.assistant.pick', '選擇同仁')} />
                )}
              />
              <Button variant="contained" disabled={!pickedAssistant} onClick={handleAddAssistant}>
                {t('calendar.assistant.add', '指定')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssistDialogOpen(false)}>{t('common.close', '關閉')}</Button>
        </DialogActions>
      </Dialog>

      {/* 事件繁忙提醒（時間衝突確認）— porsche 2026-08-27 */}
      <Dialog open={busyOpen} onClose={() => busyResolveRef.current?.(false)} maxWidth="xs" fullWidth>
        <DialogTitle>⚠️ {t('calendar.busyConflict.title', '事件繁忙提醒')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('calendar.busyConflict.description', '你要儲存的事件與以下事件時間衝突，是否仍要儲存？')}
          </Typography>
          <Stack spacing={1}>
            {busyConflicts.map((cf) => (
              <Box key={cf.id} sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{cf.summary}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtConflict(cf)}{cf.location ? ` @ ${cf.location}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => busyResolveRef.current?.(false)}>
            {t('calendar.busyConflict.cancel', '取消')}
          </Button>
          <Button onClick={() => busyResolveRef.current?.(true)} variant="contained" color="warning">
            {t('calendar.busyConflict.confirmSave', '仍要儲存')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EeMyCalendarPage;
